// src/components/WorkflowStepPanel.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Workflow, CheckCircle2, Circle, CircleDot, XCircle } from "lucide-react";
import WorkflowReassignModal, { stepAssignees } from "./WorkflowReassignModal";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

const isOverdue = (step) =>
    !!step.dueDate && step.status !== "Completed" && new Date(step.dueDate) < new Date();

const STATUS_CHIP = {
    Completed: { label: "Done", className: "bg-green-100 text-green-800", Icon: CheckCircle2 },
    Active: { label: "Current", className: "bg-indigo-100 text-indigo-800", Icon: CircleDot },
    Pending: { label: "Upcoming", className: "bg-gray-100 text-gray-700", Icon: Circle },
    Cancelled: { label: "Cancelled", className: "bg-red-100 text-red-800", Icon: XCircle },
};

const sortedSteps = (instance) =>
    [...(instance?.steps || [])].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

const primaryButton =
    "px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50";
const secondaryButton =
    "px-3 py-1.5 border border-border rounded-md text-sm font-medium text-content-primary hover:bg-theme-primary disabled:opacity-50";
const dangerButton =
    "px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-50";
const textareaClass =
    "w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan";

/**
 * Workflow panel for one remediation (custom workflows Phase 0). Loads the remediation's latest
 * workflow instance (Active, else the most recent finished one); managers can enroll / reassign /
 * cancel, the current step's assignee (or a manager) can complete it. Mirrors the
 * RemediationWorkflowsController gates: enroll / reassign / cancel = canManageOrganizationRisks OR
 * canManageDeal(dealId) OR canManageBusinessUnit(buId) (no assignment path); complete = that OR
 * the current user is any of the step's assignees (`assignedToUserIds`, falling back to the
 * legacy `assignedToUserId` via `stepAssignees`).
 *
 * Phases: loading → none (404) | active | finished | error. A finished (Completed / Cancelled)
 * instance, whether loaded or returned by a complete / cancel response, is rendered read-only with
 * a banner and managers may re-enroll beneath it. `onWorkflowStateChange(isActive, instance)` fires
 * on every transition (false for a finished instance) so the parent can hide its status controls;
 * `onLoaded()` fires exactly once, after the first load settles (any outcome), so a parent can
 * gate the section's visibility. Every action button is disabled while any request is in flight
 * and a 409 refreshes the panel. Reassign opens `WorkflowReassignModal`. An optional
 * `remediationPlan` renders as a collapsed "Remediation plan" block above everything else.
 */
const WorkflowStepPanel = ({
    riskId,
    remediationId,
    remediationTitle,
    dealId,
    businessUnitId,
    remediationPlan,
    onChanged,
    onWorkflowStateChange,
    onLoaded,
}) => {
    const apiCall = useApi();
    const { user } = useAuth();
    const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit } = useRBAC();

    // Ids of 0 mean org-level: truthiness guards the deal / BU checks.
    const isManager = !!(
        canManageOrganizationRisks()
        || (dealId && canManageDeal(dealId))
        || (businessUnitId && canManageBusinessUnit(businessUnitId))
    );

    // The parent passes inline callbacks (and the api hook is re-created per render in tests);
    // keep them in refs so the load effect depends on the remediation id alone.
    const apiRef = useRef(apiCall);
    const onChangedRef = useRef(onChanged);
    const onStateRef = useRef(onWorkflowStateChange);
    const onLoadedRef = useRef(onLoaded);
    const loadedOnceRef = useRef(false);
    useEffect(() => {
        apiRef.current = apiCall;
        onChangedRef.current = onChanged;
        onStateRef.current = onWorkflowStateChange;
        onLoadedRef.current = onLoaded;
    });

    const [phase, setPhase] = useState("loading"); // loading | none | active | finished | error
    const [instance, setInstance] = useState(null);
    const [templates, setTemplates] = useState(null);
    const [templateId, setTemplateId] = useState("");
    const [comment, setComment] = useState("");
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [reassignOpen, setReassignOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const reportState = (active, inst) => {
        if (onStateRef.current) onStateRef.current(active, inst);
    };

    const showInstance = (inst) => {
        setInstance(inst);
        setComment("");
        setCancelOpen(false);
        setCancelReason("");
        setReassignOpen(false);
        if (inst && inst.state === "Active") {
            setPhase("active");
            reportState(true, inst);
        } else {
            setPhase(inst ? "finished" : "none");
            reportState(false, inst);
        }
    };

    const load = useCallback(async () => {
        setPhase("loading");
        setError(null);
        try {
            const inst = await apiRef.current("GET", `/remediation-workflows/by-remediation/${remediationId}`);
            if (inst && inst.state === "Active") {
                setInstance(inst);
                setPhase("active");
                if (onStateRef.current) onStateRef.current(true, inst);
            } else if (inst && inst.id != null) {
                // The endpoint returns the latest finished instance when none is Active:
                // read-only stepper + banner, never treated as active (no lock, no buttons).
                setInstance(inst);
                setPhase("finished");
                if (onStateRef.current) onStateRef.current(false, inst);
            } else {
                setInstance(null);
                setPhase("none");
                if (onStateRef.current) onStateRef.current(false);
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                setInstance(null);
                setPhase("none");
                if (onStateRef.current) onStateRef.current(false);
            } else {
                setError(errorMessage(err, "The workflow could not be loaded."));
                setPhase("error");
                // Report so the parent never stays in its "unknown" state on a load failure.
                if (onStateRef.current) onStateRef.current(false);
            }
        } finally {
            // First settle only, whatever the outcome (404 and errors included).
            if (!loadedOnceRef.current) {
                loadedOnceRef.current = true;
                if (onLoadedRef.current) onLoadedRef.current();
            }
        }
    }, [remediationId]);

    useEffect(() => {
        setTemplates(null);
        setTemplateId("");
        setNotice(null);
        load();
    }, [load]);

    const canEnroll = isManager && (phase === "none" || phase === "finished");

    // Available templates are only needed for the enroll box (managers, no active workflow).
    useEffect(() => {
        if (!canEnroll || templates !== null) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiRef.current("GET", `/remediation-workflows/by-remediation/${remediationId}/available-templates`);
                if (cancelled) return;
                const list = Array.isArray(res) ? res : [];
                setTemplates(list);
                const preselected = list.find((t) => t.isDefault) || list[0];
                setTemplateId(preselected ? String(preselected.id) : "");
            } catch (err) {
                if (cancelled) return;
                setTemplates([]);
                setError(errorMessage(err, "Workflow templates could not be loaded."));
            }
        })();
        return () => { cancelled = true; };
    }, [canEnroll, templates, remediationId]);

    // Single in-flight key: every action button is disabled while any request runs.
    const run = async (key, fn, failure) => {
        if (busy) return;
        setBusy(key);
        setError(null);
        setNotice(null);
        try {
            await fn();
        } catch (err) {
            if (err?.response?.status === 409) {
                setNotice("This step was already updated; the panel has been refreshed");
                await load();
            } else {
                setError(errorMessage(err, failure));
            }
        } finally {
            setBusy(null);
        }
    };

    const notifyChanged = () => {
        if (onChangedRef.current) onChangedRef.current();
    };

    const currentStep = instance?.currentStep
        || (instance?.steps || []).find((s) => s.id === instance?.currentStepInstanceId)
        || null;
    const currentAssignees = stepAssignees(currentStep);
    const isAssignee = !!(currentStep && user?.id != null
        && currentAssignees.ids.map(String).includes(String(user.id)));
    const canComplete = isManager || isAssignee;

    const handleEnroll = () => run("enroll", async () => {
        await apiRef.current("POST", `/remediation-workflows/enroll/${remediationId}`, {
            templateId: templateId ? parseInt(templateId, 10) : null,
        });
        notifyChanged();
        await load();
    }, "The workflow could not be started.");

    // Workflow ids are per data region: send the loaded instance's region so the server acts on
    // that region's row (an id that exists in two regions is a 409 without it).
    const regionQuery = (inst) => (typeof inst?.regionId === "number" ? `?regionId=${inst.regionId}` : "");

    const handleComplete = () => {
        if (!instance || !currentStep) return;
        if (currentStep.requireComment && !comment.trim()) {
            setError("A comment is required to complete this step.");
            return;
        }
        run("complete", async () => {
            const result = await apiRef.current(
                "POST",
                `/remediation-workflows/${instance.id}/steps/${currentStep.id}/complete${regionQuery(instance)}`,
                { comment: comment.trim() || null }
            );
            notifyChanged();
            setComment("");
            if (result && result.state !== "Active") showInstance(result);
            else await load();
        }, "The step could not be completed.");
    };

    // Called by WorkflowReassignModal. Rejects on failure so the modal shows the error and stays
    // open; a 409 behaves like every other action (notice + refresh) and closes the modal.
    const handleReassign = async ({ userIds, comment }) => {
        if (!instance || !currentStep || busy) return;
        setBusy("reassign");
        setError(null);
        setNotice(null);
        try {
            await apiRef.current(
                "POST",
                `/remediation-workflows/${instance.id}/steps/${currentStep.id}/reassign${regionQuery(instance)}`,
                { userIds, comment }
            );
            notifyChanged();
            setReassignOpen(false);
            await load();
        } catch (err) {
            if (err?.response?.status === 409) {
                setReassignOpen(false);
                setNotice("This step was already updated; the panel has been refreshed");
                await load();
                return;
            }
            throw err;
        } finally {
            setBusy(null);
        }
    };

    const handleCancel = () => {
        if (!instance) return;
        if (!cancelReason.trim()) {
            setError("A reason is required to cancel the workflow.");
            return;
        }
        run("cancel", async () => {
            const result = await apiRef.current(
                "POST",
                `/remediation-workflows/${instance.id}/cancel${regionQuery(instance)}`,
                { reason: cancelReason.trim() }
            );
            notifyChanged();
            if (result && result.state !== "Active") showInstance(result);
            else await load();
        }, "The workflow could not be cancelled.");
    };

    // Optional remediation plan, collapsed by default, above everything else in every phase
    // after loading (same <details> styling as the step instructions).
    const planText = remediationPlan ? String(remediationPlan).trim() : "";
    const planBlock = planText
        ? (
            <details className="border border-border rounded-md px-3 py-2 bg-theme-primary" data-testid="workflow-remediation-plan">
                <summary className="text-xs text-indigo-600 cursor-pointer">Remediation plan</summary>
                <p className="text-xs text-content-secondary whitespace-pre-wrap mt-1">{planText}</p>
            </details>
        )
        : null;

    // Non-managers with no workflow see nothing at all (only the plan block, when there is one).
    if (phase === "loading") {
        return <div className="text-sm text-content-secondary" data-testid="workflow-panel-loading">Loading workflow...</div>;
    }
    if (phase === "error") {
        return (
            <div className="space-y-3">
                {planBlock}
                <div className="text-sm text-red-600" role="alert">{error}</div>
            </div>
        );
    }
    if (phase === "none" && !isManager) {
        // Say so rather than render a blank section a Contributor reads as hidden steps (AX-238).
        return (
            <div className="space-y-3">
                {planBlock}
                <p className="text-sm text-content-secondary" data-testid="workflow-none-readonly">
                    No workflow has been started for this remediation.
                </p>
            </div>
        );
    }

    const renderEnrollBox = () => (
        <div className="border border-border rounded-md p-3 bg-theme-primary" data-testid="workflow-enroll-box">
            <div className="flex items-center gap-2 mb-2">
                <Workflow className="h-4 w-4 text-indigo-600" />
                <span className="text-sm font-medium text-content-primary">
                    {phase === "finished" ? "Start another workflow" : "Start a workflow"}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="wf-template-select" className="sr-only">Workflow template</label>
                <select
                    id="wf-template-select"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    disabled={templates === null || !!busy}
                    className="flex-1 min-w-[12rem] px-3 py-1.5 border border-border rounded-md bg-input-bg text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan"
                >
                    {templates === null && <option value="">Loading templates...</option>}
                    {templates !== null && templates.length === 0 && <option value="">No templates available</option>}
                    {(templates || []).map((t) => (
                        <option key={t.id} value={String(t.id)}>
                            {t.name}{t.isDefault ? " (default)" : ""}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={handleEnroll}
                    disabled={!!busy || !templateId}
                    className={primaryButton}
                >
                    {busy === "enroll" ? "Starting..." : "Start workflow"}
                </button>
            </div>
        </div>
    );

    const renderStep = (step) => {
        const chip = STATUS_CHIP[step.status] || STATUS_CHIP.Pending;
        const overdue = isOverdue(step);
        const isCurrent = step.status === "Active";
        const assigneeNames = stepAssignees(step).names.filter(Boolean);
        return (
            <li
                key={step.id}
                className={`flex gap-3 py-2 ${isCurrent ? "bg-indigo-50/40 rounded-md px-2 -mx-2" : ""}`}
                data-testid={`workflow-step-${step.stepOrder}`}
            >
                <chip.Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${step.status === "Completed" ? "text-green-600" : isCurrent ? "text-indigo-600" : step.status === "Cancelled" ? "text-red-500" : "text-gray-400"}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-content-primary">
                            {step.stepOrder}. {step.name}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${chip.className}`}>
                            {chip.label}
                        </span>
                        <span className="text-xs text-content-secondary">{step.canonicalStatus}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 text-xs text-content-secondary mt-0.5">
                        <span>Assigned to: {assigneeNames.length > 0 ? assigneeNames.join(", ") : "Unassigned"}</span>
                        {step.dueDate && (
                            <span className={overdue ? "text-red-600 font-medium" : ""}>
                                Due {formatDate(step.dueDate)}{overdue ? " (overdue)" : ""}
                            </span>
                        )}
                        {step.status === "Completed" && step.completedAt && (
                            <span>
                                Completed {formatDate(step.completedAt)}{step.completedByName ? ` by ${step.completedByName}` : ""}
                            </span>
                        )}
                    </div>
                    {step.completionComment && (
                        <p className="text-xs text-content-secondary italic mt-0.5">"{step.completionComment}"</p>
                    )}
                    {step.instructions && (
                        <details className="mt-1">
                            <summary className="text-xs text-indigo-600 cursor-pointer">Instructions</summary>
                            <p className="text-xs text-content-secondary whitespace-pre-wrap mt-1">{step.instructions}</p>
                        </details>
                    )}
                    {isCurrent && phase === "active" && canComplete && renderActionBox(step)}
                </div>
            </li>
        );
    };

    const renderActionBox = (step) => (
        <div className="mt-2 border border-border rounded-md p-2 bg-theme-secondary" data-testid="workflow-action-box">
            <label htmlFor="wf-step-comment" className="block text-xs font-medium text-content-secondary mb-1">
                Comment{step.requireComment && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
                id="wf-step-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows="2"
                className={textareaClass}
                placeholder={step.requireComment ? "A comment is required to complete this step" : "Optional comment"}
            />
            <div className="flex flex-wrap items-center gap-2 mt-2">
                <button type="button" onClick={handleComplete} disabled={!!busy} className={primaryButton}>
                    {busy === "complete" ? "Completing..." : "Complete step"}
                </button>
                {isManager && (
                    <button
                        type="button"
                        onClick={() => setReassignOpen(true)}
                        disabled={!!busy}
                        className={secondaryButton}
                    >
                        {currentAssignees.ids.length > 0 ? "Reassign" : "Assign"}
                    </button>
                )}
            </div>
            {isManager && (
                <WorkflowReassignModal
                    isOpen={reassignOpen}
                    stepName={step.name}
                    hasAssignee={stepAssignees(step).ids.length > 0}
                    initialUserIds={stepAssignees(step).ids}
                    onConfirm={handleReassign}
                    onClose={() => setReassignOpen(false)}
                />
            )}
        </div>
    );

    const renderInstance = () => (
        <div className="border border-border rounded-md p-3 bg-theme-primary" data-testid="workflow-instance">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-2">
                        <Workflow className="h-4 w-4 text-indigo-600" />
                        <span className="text-sm font-semibold text-content-primary">{instance.templateName}</span>
                    </div>
                    <p className="text-xs text-content-secondary mt-0.5">
                        Started {formatDate(instance.startedAt)}{instance.startedByName ? ` by ${instance.startedByName}` : ""}
                        {remediationTitle ? ` · ${remediationTitle}` : ""}
                    </p>
                </div>
                {phase === "active" && isManager && !cancelOpen && (
                    <button type="button" onClick={() => setCancelOpen(true)} disabled={!!busy} className={dangerButton}>
                        Cancel workflow
                    </button>
                )}
            </div>

            {phase === "finished" && (
                <div
                    className={`mt-2 rounded-md px-3 py-2 text-sm ${instance.state === "Completed" ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
                    role="status"
                >
                    {instance.state === "Completed"
                        ? "Workflow completed"
                        : `Workflow cancelled: ${instance.cancelReason || ""}`}
                </div>
            )}

            {phase === "active" && isManager && cancelOpen && (
                <div className="mt-2 border border-red-200 rounded-md p-2 bg-red-50/40" data-testid="workflow-cancel-box">
                    <label htmlFor="wf-cancel-reason" className="block text-xs font-medium text-content-secondary mb-1">
                        Reason for cancelling<span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                        id="wf-cancel-reason"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows="2"
                        className={textareaClass}
                    />
                    <div className="flex gap-2 mt-2">
                        <button type="button" onClick={handleCancel} disabled={!!busy} className={dangerButton}>
                            {busy === "cancel" ? "Cancelling..." : "Confirm cancel"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setCancelOpen(false); setCancelReason(""); }}
                            disabled={!!busy}
                            className={secondaryButton}
                        >
                            Keep workflow
                        </button>
                    </div>
                </div>
            )}

            <ol className="mt-2 divide-y divide-border">
                {sortedSteps(instance).map(renderStep)}
            </ol>
            <p className="text-xs text-content-secondary mt-2">
                Completing the last step marks the remediation Completed.
            </p>
        </div>
    );

    return (
        <div className="space-y-3" data-testid="workflow-step-panel" data-risk-id={riskId}>
            {planBlock}
            {notice && <div className="text-sm text-indigo-700" role="status">{notice}</div>}
            {error && <div className="text-sm text-red-600" role="alert">{error}</div>}
            {instance && (phase === "active" || phase === "finished") && renderInstance()}
            {canEnroll && renderEnrollBox()}
        </div>
    );
};

export default WorkflowStepPanel;
